import { useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { CtaButton, SecondaryButton } from "./kit";
import { SnapshotEditor, emptySnapshot, snapshotToEditor, snapshotFromEditor, snapshotEditorValid } from "./SnapshotEditor";
import { SiteReadField } from "./SiteReadField";
import api from "../lib/api";

const DESC_PLACEHOLDER = "e.g. We help small manufacturing companies get government grants — we find the right call, prepare the application and get paid a success fee when the grant is approved. We also broker salary-backed loans for their employees through partner banks.";

export const ProfileSharpen = ({ open, onOpenChange, brain, onDone, onRegenerate }) => {
  const [step, setStep] = useState("form"); // form | extracting | confirm | done
  const [name, setName] = useState(brain?.business_name || "");
  const [desc, setDesc] = useState(brain?.business_description || "");
  const [website, setWebsite] = useState(brain?.website_url || "");
  const [snap, setSnap] = useState(emptySnapshot);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const extract = async () => {
    setStep("extracting");
    setError("");
    try {
      const { data } = await api.post("/brand-brain/snapshot/extract");
      setSnap(snapshotToEditor(data));
      setStep("confirm");
    } catch (err) {
      setError(err.response?.data?.detail || "Extraction failed — try again.");
      setStep("form");
    }
  };

  const submitForm = async () => {
    setBusy(true);
    setError("");
    try {
      await api.put("/brand-brain/extras", { business_name: name.trim(), business_description: desc.trim(), website_url: website.trim() });
      setBusy(false);
      await extract();
    } catch (err) {
      setBusy(false);
      setError(err.response?.data?.detail?.[0]?.msg || "Couldn't save — check the fields.");
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.put("/brand-brain/snapshot", snapshotFromEditor(snap));
      setBusy(false);
      setStep("done");
      onDone?.();
    } catch {
      setBusy(false);
      toast.error("Couldn't save the snapshot. Try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-2xl border-[#EAE5DB] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="profile-sharpen-dialog">
        {step === "form" && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-extrabold text-[#181621]">Sharpen your plan — 2 minutes</DialogTitle>
              <DialogDescription className="text-[13px] text-[#54505B]">
                The more precisely I know your business, the less generic your strategy gets.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-1">
              <div>
                <label className="font-mono-j text-[10px] tracking-[0.12em] text-[#54505B] uppercase block mb-1.5">Business name</label>
                <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your business name" data-testid="sharpen-name-input" />
              </div>
              <div>
                <label className="font-mono-j text-[10px] tracking-[0.12em] text-[#54505B] uppercase block mb-1.5">Describe what you actually do</label>
                <textarea
                  rows={6}
                  className="field-input resize-none"
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  placeholder={DESC_PLACEHOLDER}
                  data-testid="sharpen-desc-input"
                />
                <p className={`text-[11px] mt-1 ${desc.trim().length >= 60 ? "text-[#4B7B4F]" : "text-[#9A9591]"}`} data-testid="sharpen-desc-counter">
                  {desc.trim().length}/60 characters minimum
                </p>
              </div>
              <SiteReadField value={website} onChange={setWebsite} />
              {error && <p className="text-[13px] text-[#B4443C] bg-[#FBE9E7] rounded-lg px-3.5 py-2.5" data-testid="sharpen-error">{error}</p>}
              <CtaButton className="w-full justify-center" disabled={!name.trim() || desc.trim().length < 60 || busy} onClick={submitForm} data-testid="sharpen-submit-btn">
                {busy ? "Saving…" : "Read my business"}
              </CtaButton>
            </div>
          </>
        )}

        {step === "extracting" && (
          <div className="py-10 text-center" data-testid="sharpen-extracting">
            <span className="w-2 h-2 rounded-full bg-[#96691E] inline-block animate-pulse" />
            <p className="mt-4 text-lg font-bold text-[#181621]">Reading your description…</p>
            <p className="text-[13px] text-[#54505B] mt-1">Turning your words into a structured picture. A few seconds.</p>
          </div>
        )}

        {step === "confirm" && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-extrabold text-[#181621]">Here's how I understood your business</DialogTitle>
              <DialogDescription className="text-[13px] text-[#54505B]">Fix anything that's off — this becomes the primary truth behind every plan.</DialogDescription>
            </DialogHeader>
            <div className="mt-1">
              <SnapshotEditor snap={snap} setSnap={setSnap} />
              <div className="flex gap-2.5 mt-5">
                <SecondaryButton className="!py-2.5 !px-4 text-[13px]" onClick={extract} data-testid="sharpen-reextract-btn">
                  <RefreshCw size={13} strokeWidth={1.8} /> Re-read
                </SecondaryButton>
                <CtaButton className="flex-1 justify-center" disabled={!snapshotEditorValid(snap) || busy} onClick={confirm} data-testid="sharpen-confirm-btn">
                  {busy ? "Saving…" : "That's my business — save it"}
                </CtaButton>
              </div>
            </div>
          </>
        )}

        {step === "done" && (
          <div className="py-6 text-center" data-testid="sharpen-done">
            <Sparkles size={26} strokeWidth={1.8} className="mx-auto text-[#96691E]" />
            <p className="mt-4 text-lg font-bold text-[#181621]">Your profile just got a lot sharper.</p>
            <p className="text-[13px] text-[#54505B] mt-1">Your current plan was built on the old profile. Regenerate it to use the new one.</p>
            <div className="flex gap-2.5 mt-6 justify-center">
              <SecondaryButton className="!py-2.5 !px-4 text-[13px]" onClick={() => onOpenChange(false)} data-testid="sharpen-later-btn">
                Later
              </SecondaryButton>
              <CtaButton className="!py-2.5 !px-5 text-[13.5px]" onClick={() => { onOpenChange(false); onRegenerate?.(); }} data-testid="sharpen-regenerate-btn">
                Regenerate my plan
              </CtaButton>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
